import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PublisherCrmBadgeButton,
  PublisherCrmSlideOver,
  formatDateTimeTr,
} from "./PublisherCrmModule";

// API Endpoints
const API_PASSWORD_UPDATE = "/api/user-password-update.php";
const API_USER_NOTE_UPDATE = "/api/user-note-update.php";
const API_FEATURED_PUBLISHER_LIST = "/api/featured-publisher-list.php";
const API_FEATURED_PUBLISHER_SAVE = "/api/featured-publisher-save.php";
const API_FEATURED_PUBLISHER_DELETE = "/api/featured-publisher-delete.php";
const API_TIKTOK_PROFILE_FETCH = "/api/tiktok-profile-fetch.php";
const API_SYNC_PUBLISHER_USERNAMES = "/api/sync-publisher-usernames.php";
const API_PUBLISHER_REPORT_DIFF = "/api/publisher-report-diff.php";
const API_USER_ADD = "/api/user-add.php";
const API_USER_EDIT = "/api/user-edit.php";
const API_PUBLISHER_PERSONA_BADGES = "/api/publisher-persona-badges.php";

export async function requestJson(url, options = {}) {
  const response = await fetch(url, options);
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Sunucu hatası (${response.status})`);
  }
  return data;
}

export function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

export function Card({ children, className = "" }) {
  return (
    <div className={`rounded-[1.45rem] border border-white/[0.08] bg-white/[0.03] shadow-[0_18px_60px_rgba(0,0,0,.32)] ${className}`}>
      {children}
    </div>
  );
}

export function Pill({ children, tone = "default" }) {
  const toneClass =
    tone === "gold"
      ? "border-yellow-500/30 bg-yellow-500/15 text-yellow-200"
      : tone === "red"
      ? "border-red-500/25 bg-red-500/10 text-red-300"
      : tone === "green"
      ? "border-green-500/25 bg-green-500/10 text-green-300"
      : "border-white/10 bg-white/[0.035] text-white/45";
  return (
    <span className={`rounded-full border px-3 py-1 text-[11px] font-bold ${toneClass}`}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  const tone = status === "Aktif" ? "green" : status === "Eksik" || status === "Pasif" ? "red" : "gold";
  return <Pill tone={tone}>{status || "-"}</Pill>;
}

export function SectionHeader({ eyebrow, title, desc, action }) {
  return (
    <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.36em] text-yellow-300/80">{eyebrow}</div>
        <h2 className="mt-2 text-2xl font-black tracking-tight text-white lg:text-3xl">{title}</h2>
        <p className="mt-2 max-w-4xl text-[13px] leading-6 text-white/46">{desc}</p>
      </div>
      {action}
    </div>
  );
}

export function ActionButton({ children, variant = "gold", onClick, type = "button", disabled = false }) {
  const cls =
    variant === "gold"
      ? "border-yellow-500/30 bg-yellow-500 text-black hover:bg-yellow-400"
      : "border-white/10 bg-white/[0.035] text-white/65 hover:border-yellow-500/30 hover:text-yellow-300";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl border px-4 py-2.5 text-xs font-black transition disabled:cursor-not-allowed disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}

export function RowActionButton({ children, danger = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
        danger
          ? "border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
          : "border-white/10 bg-white/[0.035] text-white/68 hover:border-yellow-500/30 hover:text-yellow-300"
      }`}
    >
      {children}
    </button>
  );
}

export function SearchBox({ placeholder, value, onChange }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
      <span className="text-white/35">⌕</span>
      <input
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/30"
      />
    </div>
  );
}

export function ApiNotice({ loading, error, onReload }) {
  if (loading) return <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-2 text-xs font-semibold text-white/45">API verisi yükleniyor...</div>;
  if (!error) return null;
  return (
    <div className="mt-3 flex flex-col gap-3 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-xs font-semibold text-red-200 sm:flex-row sm:items-center sm:justify-between">
      <span>{error}</span>
      <button type="button" onClick={onReload} className="rounded-lg border border-red-300/20 px-3 py-1.5 text-red-100 hover:bg-red-500/15">Tekrar Dene</button>
    </div>
  );
}

export function EmptyState({ text }) {
  return <div className="border-t border-white/10 p-6 text-sm text-white/45">{text}</div>;
}

export function FormInput({ label, name, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold text-white/45">{label}</span>
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder || label}
        className="w-full rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-yellow-500/35"
      />
    </label>
  );
}

export function FormSelect({ label, name, value, onChange, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold text-white/45">{label}</span>
      <select
        name={name}
        value={value || ""}
        onChange={onChange}
        className="w-full rounded-2xl border border-white/10 bg-[#101010] px-4 py-3 text-sm text-white outline-none focus:border-yellow-500/35"
      >
        {children}
      </select>
    </label>
  );
}

export function ModalShell({ title, subtitle, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className={`max-h-[92vh] w-full overflow-y-auto rounded-[2rem] border border-white/10 bg-[#0b0b0b] p-6 shadow-[0_20px_80px_rgba(0,0,0,0.55)] ${wide ? "max-w-3xl" : "max-w-xl"}`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl font-black text-white">{title}</h3>
            {subtitle ? <p className="mt-2 text-sm text-white/50">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 px-3 py-2 text-sm text-white/70 hover:border-yellow-500/35 hover:text-yellow-300"
          >
            Kapat
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ModalActions({ onClose, saving, submitText }) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <ActionButton variant="dark" onClick={onClose}>İptal</ActionButton>
      <ActionButton type="submit" disabled={saving}>{saving ? "Kaydediliyor..." : submitText}</ActionButton>
    </div>
  );
}

export function StatCard({ title, value, note }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">{title}</div>
      <div className="mt-2 text-2xl font-black text-white truncate">{value}</div>
      {note && <div className="mt-1 text-xs text-white/45 truncate">{note}</div>}
    </Card>
  );
}

export function PasswordModal({ open, target, form, saving, onChange, onClose, onSubmit }) {
  if (!open || !target) return null;
  return (
    <ModalShell title="Şifre Düzenle" subtitle={`Kullanıcı: ${target.username || target.fullName || "-"}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="grid gap-4">
        <FormInput label="Yeni şifre" name="newPassword" type="password" value={form.newPassword} onChange={onChange} />
        <FormInput label="Yeni şifre tekrar" name="confirmPassword" type="password" value={form.confirmPassword} onChange={onChange} />
        <ModalActions onClose={onClose} saving={saving} submitText="Şifreyi Kaydet" />
      </form>
    </ModalShell>
  );
}

// Standart Yayıncı Modal (Ekle / Düzenle)
export function StandardPublisherModal({ mode, open, form, setForm, managers = [], saving, onChange, onFileChange, onClose, onSubmit }) {
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [tiktokMsg, setTiktokMsg] = useState("");

  if (!open) return null;
  const isEdit = mode === "edit";

  async function handleFetchTikTok() {
    const userToFetch = form.username?.trim();
    if (!userToFetch) {
      alert("Lütfen önce TikTok kullanıcı adını giriniz.");
      return;
    }

    try {
      setTiktokLoading(true);
      setTiktokMsg("TikTok profili taranıyor ve fotoğraf indiriliyor...");
      const res = await requestJson(`${API_TIKTOK_PROFILE_FETCH}?username=${encodeURIComponent(userToFetch)}`);
      
      if (res && res.success && res.data) {
        const d = res.data;
        if (setForm) {
          setForm((prev) => ({
            ...prev,
            fullName: prev.fullName || d.fullName || d.nickname || prev.username,
            image: d.avatar || d.avatarRemote || prev.image,
            isFeatured: true,
          }));
        }
        const followerTxt = d.followerCount > 0 ? ` • ${Number(d.followerCount).toLocaleString("tr-TR")} Takipçi` : "";
        setTiktokMsg(`✅ Başarılı! @${d.username} profili ve fotoğrafı aktarıldı.${followerTxt}`);
      } else {
        setTiktokMsg(res.message || "TikTok profili bulunamadı.");
      }
    } catch (err) {
      console.error(err);
      setTiktokMsg("Bağlantı hatası: Profil çekilemedi.");
    } finally {
      setTiktokLoading(false);
    }
  }

  return (
    <ModalShell title={isEdit ? "Yayıncı Düzenle" : "Yayıncı Ekle"} onClose={onClose}>
      <form onSubmit={onSubmit} className="grid gap-4">
        <FormInput label="Ad Soyadı" name="fullName" value={form.fullName} onChange={onChange} />
        
        <div>
          <FormInput 
            label="Sistem Giriş Adı (Ajans Giriş ID)" 
            name="system_username" 
            value={form.system_username || ""} 
            onChange={onChange} 
            placeholder="ornek: wolf.burakgaming" 
          />
          <div className="mt-1 text-[11px] text-yellow-300/80">
            💡 Yayıncının ajans paneline girişte kullanacağı kalıcı kullanıcı adıdır.
          </div>
        </div>

        <div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <FormInput label="TikTok Kullanıcı Adı" name="username" value={form.username} onChange={onChange} placeholder="ornek: burakgaming" />
            </div>
            <button
              type="button"
              disabled={tiktokLoading || !form.username}
              onClick={handleFetchTikTok}
              className="mb-0.5 inline-flex h-11 items-center gap-1.5 rounded-xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/20 via-amber-500/15 to-yellow-500/20 px-3.5 text-xs font-black text-yellow-200 transition hover:border-yellow-400 hover:bg-yellow-500/30 hover:scale-105 active:scale-95 disabled:opacity-50"
              title="TikTok profil fotoğrafı, isim ve bilgileri otomatik çek"
            >
              {tiktokLoading ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-yellow-400 border-t-transparent" />
              ) : (
                <span className="text-sm">✨</span>
              )}
              <span>{tiktokLoading ? "Çekiliyor..." : "TikTok'tan Getir"}</span>
            </button>
          </div>
          {tiktokMsg && (
            <div className={`mt-1.5 text-xs font-semibold ${tiktokMsg.includes("✅") ? "text-emerald-400" : "text-amber-300"}`}>
              {tiktokMsg}
            </div>
          )}
        </div>

        <FormInput label="Telefon" name="phone" value={form.phone} onChange={onChange} placeholder="05XXXXXXXXX" />
        <FormInput label="TikTok Hidden ID" name="hiddenId" value={form.hiddenId || ""} onChange={onChange} placeholder="7486099..." />
        
        {!isEdit && (
          <FormInput label="Geçici Şifre" name="password" type="password" value={form.password || ""} onChange={onChange} placeholder="En az 6 karakter" />
        )}

        {managers.length > 0 && (
          <FormSelect label="Sorumlu Yönetici" name="assignedEmployeeUsername" value={form.assignedEmployeeUsername || ""} onChange={onChange}>
            <option value="">Yönetici Seçiniz</option>
            {managers.map((m) => (
              <option key={m.username} value={m.username}>{m.fullName || m.username}</option>
            ))}
          </FormSelect>
        )}

        <FormSelect label="Ajans Takibi" name="agencyTrackingStatus" value={form.agencyTrackingStatus || "Aktif"} onChange={onChange}>
          <option value="Aktif">Aktif</option>
          <option value="Pasif">Pasif</option>
        </FormSelect>
        <FormSelect label="Durum" name="status" value={form.status || "Aktif"} onChange={onChange}>
          <option value="Aktif">Aktif</option>
          <option value="Pasif">Pasif</option>
        </FormSelect>
        <ModalActions onClose={onClose} saving={saving} submitText="Kaydet ve Yayınla" />
      </form>
    </ModalShell>
  );
}

// 1. YAYINCI LİSTESİ (YÖNETİCİYE ÖZEL: SİL VE DÜZENLE YOK, SADECE ŞİFRE DEĞİŞTİRME YETKİSİ VAR)
export function PublisherListContent({
  rows = [],
  managers = [],
  loading = false,
  error = "",
  onReload,
  onPassword,
  canEdit = false,
  canDelete = false,
  onEdit = null,
  onDelete = null,
  onAdd = null,
  onBulk = null,
  currentAdmin = {},
  theme,
}) {
  const [search, setSearch] = useState("");
  const [featuredList, setFeaturedList] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [noteModal, setNoteModal] = useState({ open: false, user: null, text: "", saving: false, error: "" });

  const isDark = theme === "dark" || (!theme && typeof document !== "undefined" && !document.querySelector(".wm-admin-light"));

  const loadFeaturedList = useCallback(async () => {
    try {
      setFeaturedLoading(true);
      const res = await requestJson(API_FEATURED_PUBLISHER_LIST, { cache: "no-store" });
      setFeaturedList(Array.isArray(res.publishers) ? res.publishers : []);
    } catch (e) {
      console.warn("Öne çıkan yayıncılar listesi alınamadı:", e);
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeaturedList();
  }, [loadFeaturedList]);

  async function handleToggleFeatured(user) {
    const userCleanUser = String(user.username || "").replace(/[@\s]+/g, "").toLowerCase();
    const isFeatured = featuredList.some((fp) => {
      const fpCleanUser = String(fp.username || "").replace(/[@\s]+/g, "").toLowerCase();
      return (
        (fpCleanUser && userCleanUser && fpCleanUser === userCleanUser) ||
        String(fp.id) === String(user.id)
      );
    });

    try {
      setTogglingId(user.id);
      if (isFeatured) {
        await requestJson(API_FEATURED_PUBLISHER_DELETE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: user.id, username: user.username }),
        });
      } else {
        await requestJson(API_FEATURED_PUBLISHER_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            username: user.username,
            fullName: user.fullName,
            image: user.image || user.profileImage || "",
            diamonds: user.performanceDiamonds || user.currentDiamonds || 0,
            liveHours: user.liveHours || 0,
            rank: 99,
          }),
        });
      }
      await loadFeaturedList();
    } catch (e) {
      alert("Öne çıkan durumu değiştirilemedi: " + (e.message || "Hata"));
    } finally {
      setTogglingId(null);
    }
  }

  async function handleSyncAllTikTokPhotos() {
    if (!window.confirm("Tüm yayıncıların TikTok fotoğrafları taranıp kaydedilsin mi?")) return;
    try {
      setSyncingAll(true);
      setSyncProgress({ current: 0, total: rows.length, currentName: "Başlatılıyor...", successCount: 0 });
      let success = 0;
      for (let i = 0; i < rows.length; i++) {
        const u = rows[i];
        const rawUser = String(u.username || "").replace(/[@\s]+/g, "").trim();
        if (rawUser) {
          setSyncProgress({ current: i + 1, total: rows.length, currentName: `@${rawUser}`, successCount: success });
          try {
            const res = await requestJson(`${API_TIKTOK_PROFILE_FETCH}?username=${encodeURIComponent(rawUser)}&autoSave=1`);
            if (res && res.success && res.data?.avatar) success++;
          } catch {}
        }
      }
      setSyncProgress((prev) => prev ? { ...prev, currentName: "Tamamlandı!" } : null);
      if (onReload) onReload();
      loadFeaturedList();
    } catch (e) {
      alert("Fotoğraf tarama hatası: " + e.message);
    } finally {
      setSyncingAll(false);
      setTimeout(() => setSyncProgress(null), 4000);
    }
  }

  function openNoteModal(user) {
    const currentNote = notesMap[user.id] !== undefined ? notesMap[user.id] : (user.note || user.raw?.note || "");
    setNoteModal({ open: true, user, text: currentNote, saving: false, error: "" });
  }

  async function persistNoteToServer(user, noteText) {
    const payload = JSON.stringify({
      id: user.id,
      userId: user.id,
      username: user.username,
      note: noteText,
      action: "save_note",
    });

    // 1. Try dedicated note update endpoint
    try {
      const res = await fetch(API_USER_NOTE_UPDATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      const text = await res.text();
      if (text && text.trim().startsWith("{")) {
        const data = JSON.parse(text);
        if (data && data.success) return data;
      }
    } catch (e1) {
      console.warn("API_USER_NOTE_UPDATE failed, trying fallback:", e1);
    }

    // 2. Try users-list.php POST fallback
    try {
      const res = await fetch("/api/users-list.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      const text = await res.text();
      if (text && text.trim().startsWith("{")) {
        const data = JSON.parse(text);
        if (data && data.success) return data;
      }
    } catch (e2) {
      console.warn("users-list.php POST fallback failed:", e2);
    }

    // 3. Try user-update.php fallback
    try {
      const res = await fetch("/api/user-update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      const text = await res.text();
      if (text && text.trim().startsWith("{")) {
        const data = JSON.parse(text);
        if (data && data.success) return data;
      }
    } catch (e3) {
      console.warn("user-update.php fallback failed:", e3);
    }

    throw new Error("Sunucuya bağlanılamadı veya not kaydedilemedi. Lütfen bağlantınızı kontrol edin.");
  }

  async function handleSaveNote() {
    if (!noteModal.user) return;
    try {
      setNoteModal((prev) => ({ ...prev, saving: true, error: "" }));
      const savedNote = noteModal.text.trim();
      await persistNoteToServer(noteModal.user, savedNote);

      setNotesMap((prev) => ({ ...prev, [noteModal.user.id]: savedNote }));
      if (noteModal.user) {
        noteModal.user.note = savedNote;
        if (noteModal.user.raw) noteModal.user.raw.note = savedNote;
      }
      setNoteModal({ open: false, user: null, text: "", saving: false, error: "" });
    } catch (err) {
      setNoteModal((prev) => ({ ...prev, saving: false, error: err.message || "Hata oluştu" }));
    }
  }

  async function handleDeleteNote() {
    if (!noteModal.user) return;
    if (!window.confirm("Bu yayıncının notunu kalıcı olarak silmek istediğinize emin misiniz?")) return;
    try {
      setNoteModal((prev) => ({ ...prev, saving: true, error: "" }));
      await persistNoteToServer(noteModal.user, "");

      setNotesMap((prev) => ({ ...prev, [noteModal.user.id]: "" }));
      if (noteModal.user) {
        noteModal.user.note = "";
        if (noteModal.user.raw) noteModal.user.raw.note = "";
      }
      setNoteModal({ open: false, user: null, text: "", saving: false, error: "" });
    } catch (err) {
      setNoteModal((prev) => ({ ...prev, saving: false, error: err.message || "Silinemedi." }));
    }
  }

  const filteredRows = useMemo(() => {
    return rows.filter((item) => {
      const q = search.toLowerCase().trim();
      if (!q) return true;
      const userNote = notesMap[item.id] !== undefined ? notesMap[item.id] : (item.note || item.raw?.note || "");
      return `${item.fullName} ${item.username} ${item.phone} ${item.hiddenId || item.id} ${item.managerName || item.assignedEmployeeUsername || item.representative} ${userNote}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search, notesMap]);

  return (
    <div style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}>
      <SectionHeader
        eyebrow="Yönetim Paneli"
        title="Yayıncı Paneli"
        desc="Bağlı yayıncılarınızın giriş bilgileri, kullanıcı adı, profil görseli, telefon, ID ve durum bilgileri bu ekranda yönetilir."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={syncingAll || rows.length === 0}
              onClick={handleSyncAllTikTokPhotos}
              className="inline-flex items-center gap-2 rounded-xl border border-yellow-500/50 bg-gradient-to-r from-yellow-500/25 via-amber-500/20 to-yellow-500/25 px-3.5 py-2 text-xs font-black text-yellow-200 shadow-sm transition hover:border-yellow-400 hover:bg-yellow-500/35 hover:scale-105 active:scale-95 disabled:opacity-50"
              title="Bağlı yayıncıların TikTok fotoğraflarını tek tıkla otomatik çek ve güncelle"
            >
              {syncingAll ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-yellow-400 border-t-transparent" />
              ) : (
                <span className="text-sm">⚡</span>
              )}
              <span>{syncingAll ? `Çekiliyor (%${syncProgress ? Math.round((syncProgress.current / syncProgress.total) * 100) : 0})...` : "TikTok Fotoğraflarını Çek"}</span>
            </button>
            <ActionButton variant="dark" onClick={loadFeaturedList}>Öne Çıkanları Yenile</ActionButton>
            {onBulk && <ActionButton variant="dark" onClick={onBulk}>Toplu Yükle</ActionButton>}
            {onAdd && <ActionButton onClick={onAdd}>Yeni Yayıncı</ActionButton>}
          </div>
        }
      />

      {syncProgress && (
        <div className="mb-4 rounded-2xl border border-yellow-500/40 bg-yellow-500/10 p-4 shadow-[0_0_20px_rgba(234,179,8,0.15)]">
          <div className="flex items-center justify-between text-xs font-black text-yellow-300">
            <span className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-yellow-400 border-t-transparent" />
              TikTok Fotoğrafı İndiriliyor: <strong className={isDark ? "text-white" : "text-slate-900"}>{syncProgress.currentName}</strong>
            </span>
            <span>{syncProgress.current} / {syncProgress.total} (%{Math.round((syncProgress.current / syncProgress.total) * 100)})</span>
          </div>
          <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-black/40 border border-white/10">
            <div
              className="h-full bg-gradient-to-r from-yellow-500 to-amber-400 transition-all duration-300"
              style={{ width: `${Math.round((syncProgress.current / syncProgress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}

      <div className={`rounded-2xl border shadow-sm overflow-hidden transition-colors ${
        isDark ? "bg-[#121620] border-[#1e2838]" : "bg-[#f4f7fb] border-[#e2e8f0]"
      }`}>
        <div className={`p-4 border-b ${isDark ? "border-[#1e2838] bg-[#121620]" : "border-[#e2e8f0] bg-[#f4f7fb]"}`}>
          <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Yayıncı ara (Ad, kullanıcı adı, telefon, ID)..." />
          <ApiNotice loading={loading || featuredLoading} error={error} onReload={() => { if (onReload) onReload(); loadFeaturedList(); }} />
        </div>

        <div className="overflow-x-auto min-w-full">
          <div className="min-w-[1100px]">
            {/* TABLO SÜTUN BAŞLIKLARI - 8 SÜTUN */}
            <div className={`hidden xl:grid grid-cols-[1.5fr_1.1fr_0.9fr_1.3fr_0.9fr_0.75fr_0.9fr_auto] gap-3 px-5 py-3.5 text-[12px] font-[600] uppercase tracking-[0.06em] border-b items-center ${
              isDark ? "bg-[#182030] border-[#1e2838] text-[#8fa2be]" : "bg-[#edf3fa] border-[#e2e8f0] text-[#405674]"
            }`}>
              <div>AD SOYADI & GÖRSEL</div>
              <div>GİRİŞ ADI / TIKTOK</div>
              <div>TELEFON</div>
              <div>ID</div>
              <div>YÖNETİCİ</div>
              <div>AJANS TAKİBİ</div>
              <div>NOT</div>
              <div className="text-left">İŞLEMLER</div>
            </div>

            {!loading && filteredRows.length === 0 ? (
              <EmptyState text="Bağlı yayıncı kaydı bulunamadı." />
            ) : (
              filteredRows.map((user) => {
                const userCleanUser = String(user.username || "").replace(/[@\s]+/g, "").toLowerCase();
                const matchedFeatured = featuredList.find((fp) => {
                  const fpCleanUser = String(fp.username || "").replace(/[@\s]+/g, "").toLowerCase();
                  return (
                    (fpCleanUser && userCleanUser && fpCleanUser === userCleanUser) ||
                    String(fp.id) === String(user.id)
                  );
                });
                const isFeatured = Boolean(matchedFeatured);
                const publisherImg = user.image || user.profileImage || matchedFeatured?.image || matchedFeatured?.currentImage;
                const userNote = notesMap[user.id] !== undefined ? notesMap[user.id] : (user.note || user.raw?.note || "");
                const agencyTrackingStatus = user.agencyTrackingStatus || (user.agency_tracking_status ? (user.agency_tracking_status === "Aktif" ? "Aktif" : "Pasif") : "Aktif");

                return (
                  <div
                    key={user.id}
                    className={`grid grid-cols-[1.5fr_1.1fr_0.9fr_1.3fr_0.9fr_0.75fr_0.9fr_auto] gap-3 px-5 py-3.5 text-[14px] items-center border-b transition-colors ${
                      isDark
                        ? "bg-[#131925] border-[#1c2536] text-[#cbd5e1] hover:bg-[#1a2335]"
                        : "bg-white border-[#eef2f7] text-[#334155] hover:bg-[#ebf1f8]"
                    }`}
                  >
                    {/* 1. AD SOYADI & GÖRSEL */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        {publisherImg ? (
                          <img
                            src={publisherImg}
                            alt={user.fullName}
                            className={`h-10 w-10 rounded-full object-cover border ${
                              isDark ? "border-[#2a374e]" : "border-[#cbd5e1]"
                            }`}
                          />
                        ) : (
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                            isDark
                              ? "border-[#2a374e] bg-[#1c2434] text-yellow-300"
                              : "border-[#cbd5e1] bg-[#eef2f6] text-slate-700"
                          }`}>
                            {user.fullName?.slice(0, 2)?.toUpperCase() || "WM"}
                          </div>
                        )}
                        {isFeatured && (
                          <span
                            className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[9px] shadow-sm"
                            title="Öne Çıkarıldı"
                          >
                            ⭐
                          </span>
                        )}
                      </div>

                      <div className="min-w-0">
                        <div className={`font-[600] text-[14px] truncate ${isDark ? "text-[#f8fafc]" : "text-[#0f172a]"}`}>
                          {user.fullName || "-"}
                        </div>
                        <div className={`text-[12px] truncate ${isDark ? "text-[#8fa2be]" : "text-[#64748b]"}`}>
                          @{user.username || "-"}
                        </div>
                      </div>
                    </div>

                    {/* 2. GİRİŞ ADI / TIKTOK */}
                    <div className="flex flex-col justify-center min-w-0">
                      <span
                        className={`font-mono text-[12px] font-semibold truncate ${
                          isDark ? "text-[#38bdf8]" : "text-[#0284c7]"
                        }`}
                        title={`Sistem Giriş Adı: ${user.system_username || user.systemUsername || ("wolf." + userCleanUser)}`}
                      >
                        {user.system_username || user.systemUsername || ("wolf." + userCleanUser)}
                      </span>
                      <span className={`text-[12px] font-normal truncate mt-0.5 ${
                        isDark ? "text-[#8fa2be]" : "text-[#64748b]"
                      }`}>
                        @{user.username || "-"}
                      </span>
                    </div>

                    {/* 3. TELEFON */}
                    <div className={`font-mono tabular-nums text-[14px] whitespace-nowrap ${
                      isDark ? "text-[#e2e8f0]" : "text-[#1e293b]"
                    }`}>
                      {user.phone || "-"}
                    </div>

                    {/* 4. ID (Eksiksiz, rakamlar hizalı, kesilme yok) */}
                    <div className={`font-mono tabular-nums text-[14px] select-all whitespace-nowrap ${
                      isDark ? "text-[#e2e8f0]" : "text-[#1e293b]"
                    }`}>
                      {user.hiddenId || user.tiktok_id || user.id || "-"}
                    </div>

                    {/* 5. YÖNETİCİ */}
                    <div className={`text-[14px] truncate ${isDark ? "text-[#e2e8f0]" : "text-[#1e293b]"}`}>
                      {user.managerName || user.assignedEmployeeUsername || user.representative || "-"}
                    </div>

                    {/* 6. AJANS TAKİBİ (Düz metin: Rozet, çerçeve, nokta veya renkli hap yok) */}
                    <div className={`text-[14px] font-normal ${isDark ? "text-[#e2e8f0]" : "text-[#334155]"}`}>
                      {agencyTrackingStatus || "Aktif"}
                    </div>

                    {/* 7. NOT (Ajans Takibi ile İşlemler arasında) */}
                    <div className="flex items-center min-w-0">
                      {userNote ? (
                        <div className="flex items-center gap-1.5 min-w-0 max-w-[130px] 2xl:max-w-[160px]">
                          <span
                            onClick={() => openNoteModal(user)}
                            className={`truncate cursor-pointer text-[13px] font-normal transition ${
                              isDark ? "text-[#cbd5e1] hover:text-white hover:underline" : "text-[#334155] hover:text-[#0f172a] hover:underline"
                            }`}
                            title={`${userNote} (Düzenlemek için tıklayın)`}
                          >
                            {userNote}
                          </span>
                          <button
                            type="button"
                            onClick={() => openNoteModal(user)}
                            title="Notu düzenle"
                            className="text-[11px] opacity-40 hover:opacity-100 transition shrink-0"
                          >
                            ✏️
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openNoteModal(user)}
                          className={`text-[13px] font-normal transition underline-offset-2 hover:underline ${
                            isDark ? "text-[#8fa2be] hover:text-white" : "text-[#556987] hover:text-[#1e293b]"
                          }`}
                        >
                          Not ekle
                        </button>
                      )}
                    </div>

                    {/* 8. İŞLEMLER */}
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      {canEdit && onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(user)}
                          className={`rounded-[7px] px-2.5 py-1 text-[12px] font-medium border transition ${
                            isDark
                              ? "bg-[#1c2434] border-[#2e3c54] text-[#d1d5db] hover:bg-[#253248] hover:text-white"
                              : "bg-white border-[#cbd5e1] text-[#334155] hover:bg-[#f1f5f9] shadow-sm"
                          }`}
                        >
                          Düzenle
                        </button>
                      )}

                      {onPassword && (
                        <button
                          type="button"
                          onClick={() => onPassword(user)}
                          className={`rounded-[7px] px-2.5 py-1 text-[12px] font-medium border transition ${
                            isDark
                              ? "bg-[#1c2434] border-[#2e3c54] text-[#d1d5db] hover:bg-[#253248] hover:text-white"
                              : "bg-white border-[#cbd5e1] text-[#334155] hover:bg-[#f1f5f9] shadow-sm"
                          }`}
                        >
                          Şifre
                        </button>
                      )}

                      {canDelete && onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(user, "publisher")}
                          className={`rounded-[7px] px-2.5 py-1 text-[12px] font-medium border transition ${
                            isDark
                              ? "bg-[#2a1b22] border-[#522933] text-[#f87171] hover:bg-[#38222c] hover:border-[#6b2f3d]"
                              : "bg-[#fff1f2] border-[#fecdd3] text-[#e11d48] hover:bg-[#ffe4e6] shadow-sm"
                          }`}
                        >
                          Sil
                        </button>
                      )}

                      <button
                        type="button"
                        disabled={togglingId === user.id}
                        onClick={() => handleToggleFeatured(user)}
                        className={`rounded-[7px] px-2.5 py-1 text-[12px] font-medium border transition ${
                          isFeatured
                            ? isDark
                              ? "bg-[#332b17] border-[#785e23] text-[#fef08a] hover:bg-[#42371c]"
                              : "bg-[#fefce8] border-[#fde047] text-[#a16207] hover:bg-[#fef9c3] shadow-sm"
                            : isDark
                            ? "bg-[#242018] border-[#4a3b1a] text-[#fbbf24] hover:bg-[#302a1e] hover:border-[#634e20]"
                            : "bg-[#fffbeb] border-[#fcd34d] text-[#b45309] hover:bg-[#fef3c7] shadow-sm"
                        }`}
                        title={isFeatured ? "Öne çıkanlardan kaldır" : "Anasayfa öne çıkan yayıncılara ekle"}
                      >
                        {togglingId === user.id ? "..." : isFeatured ? "⭐ Öne Çıkarıldı" : "⭐ Öne Çıkar"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* NOT DÜZENLEME MODALI */}
      {noteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div
            className={`w-full max-w-md rounded-2xl p-5 shadow-2xl border transition-all ${
              isDark ? "bg-[#161c28] border-[#253248] text-white" : "bg-white border-slate-200 text-slate-800"
            }`}
            style={{ fontFamily: 'Inter, "Segoe UI", sans-serif' }}
          >
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <div>
                <h3 className="text-sm font-semibold">Yayıncı Notu</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {noteModal.user?.fullName} (@{noteModal.user?.username})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNoteModal({ open: false, user: null, text: "", saving: false, error: "" })}
                className="text-slate-400 hover:text-white text-lg font-bold px-1"
              >
                ✕
              </button>
            </div>

            <div className="py-4">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                Not İçeriği
              </label>
              <textarea
                autoFocus
                rows={4}
                value={noteModal.text}
                onChange={(e) => setNoteModal((prev) => ({ ...prev, text: e.target.value }))}
                placeholder="Yayıncı ile ilgili notunuzu yazın..."
                className={`w-full rounded-xl p-3 text-sm border focus:outline-none focus:ring-2 transition-all resize-none ${
                  isDark
                    ? "bg-[#0f131c] border-[#222d40] text-slate-100 focus:border-blue-500/60 focus:ring-blue-500/20"
                    : "bg-slate-50 border-slate-200 text-slate-800 focus:border-blue-500/60 focus:ring-blue-500/20"
                }`}
              />
              <p className={`mt-1.5 text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                💡 Notlar yayıncı özelinde kalıcı olarak saklanır. Yönetici veya admin değiştirmediği sürece silinmez veya kaybolmaz.
              </p>
              {noteModal.error && (
                <div className="mt-2 text-xs font-medium text-red-400">
                  {noteModal.error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              {Boolean(notesMap[noteModal.user?.id] || noteModal.user?.note || noteModal.user?.raw?.note) ? (
                <button
                  type="button"
                  disabled={noteModal.saving}
                  onClick={handleDeleteNote}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition disabled:opacity-50"
                >
                  Notu Sil
                </button>
              ) : <div />}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={noteModal.saving}
                  onClick={() => setNoteModal({ open: false, user: null, text: "", saving: false, error: "" })}
                  className={`px-3.5 py-1.5 text-xs font-medium rounded-lg border transition ${
                    isDark
                      ? "border-[#253248] bg-[#1a2233] text-slate-300 hover:bg-[#222c42]"
                      : "border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  disabled={noteModal.saving}
                  onClick={handleSaveNote}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition disabled:opacity-50"
                >
                  {noteModal.saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 2. YAYINCI TAKİBİ (AJANS ONAY VE CRM GÖRÜŞME KAYDI)
export function PublisherTrackingContent({ publisherRows = [], managers = [], loading = false, onReload, currentAdmin = {} }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCrmUser, setSelectedCrmUser] = useState(null);

  const filteredRows = useMemo(() => {
    return (publisherRows || []).filter((item) => {
      const q = search.toLowerCase().trim();
      const matchSearch = !q || `${item.fullName} ${item.username} ${item.hiddenId} ${item.phone}`.toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || item.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [publisherRows, search, statusFilter]);

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Yayıncı Takip Modülü"
        title="Yayıncı Takibi"
        desc="Bağlı yayıncılarınızın günlük yayın disiplini, yayın süresi, elmas hedefleri ve görüşme kayıtları (CRM) bu ekrandan yürütülür."
        action={
          <ActionButton onClick={onReload}>
            {loading ? "Yenileniyor..." : "Takip Listesini Yenile"}
          </ActionButton>
        }
      />

      <Card className="overflow-hidden">
        <div className="border-b border-white/10 p-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
          <div className="w-full sm:w-80">
            <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Yayıncı, TikTok ID veya telefon ara..." />
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-xs font-semibold text-white outline-none [color-scheme:dark]"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="Aktif">Aktif</option>
              <option value="Pasif">Pasif</option>
            </select>
          </div>
        </div>

        <div className="hidden grid-cols-[1.2fr_1fr_1fr_1fr_1fr_1fr_.8fr_1.1fr] gap-3 bg-white/[0.04] px-5 py-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white/38 xl:grid">
          <div>Yayıncı Adı</div>
          <div>Kullanıcı Adı</div>
          <div>TikTok Hidden ID</div>
          <div>Sorumlu Yönetici</div>
          <div>Aylık Elmas</div>
          <div>Canlı Süre / Gün</div>
          <div>Durum</div>
          <div>İletişim & CRM</div>
        </div>

        {!loading && filteredRows.length === 0 ? (
          <EmptyState text="Eşleşen takip kaydı bulunamadı." />
        ) : (
          filteredRows.map((user) => (
            <div
              key={user.id || user.username}
              className="grid gap-3 border-t border-white/10 px-5 py-4 text-sm text-white/68 hover:bg-yellow-500/[0.025] xl:grid-cols-[1.2fr_1fr_1fr_1fr_1fr_1fr_.8fr_1.1fr] items-center"
            >
              <div className="font-bold text-white flex items-center gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 text-xs font-black text-amber-300 border border-amber-500/30">
                  {String(user.fullName || user.username || "Y").charAt(0).toUpperCase()}
                </div>
                <span className="truncate">{user.fullName || "-"}</span>
              </div>
              <div className="font-mono text-xs text-amber-300 truncate">@{String(user.username || "").replace(/^@+/, "")}</div>
              <div className="font-mono text-xs text-cyan-300 truncate">{user.hiddenId || "-"}</div>
              <div className="text-xs text-white/80 truncate">
                {user.assignedEmployeeUsername || user.managerName || user.representative || "Atanmadı"}
              </div>
              <div className="font-mono text-xs text-emerald-400 font-bold">
                💎 {Number(user.performanceDiamonds ?? user.currentDiamonds ?? 0).toLocaleString("tr-TR")}
              </div>
              <div className="text-xs text-white/70 font-mono">
                {user.liveHours || user.liveDuration || "-"} / {user.liveDays || user.validDays || "-"} Gün
              </div>
              <div>
                <StatusBadge status={user.status || "Aktif"} />
              </div>
              <div>
                <PublisherCrmBadgeButton
                  publisher={user}
                  onClick={() => setSelectedCrmUser(user)}
                />
              </div>
            </div>
          ))
        )}
      </Card>

      {/* YANDA AÇILIP KAPANAN SLIDE-OVER CRM GÖRÜŞME MASASI */}
      <PublisherCrmSlideOver
        publisher={selectedCrmUser}
        currentUser={currentAdmin}
        isOpen={Boolean(selectedCrmUser)}
        onClose={() => setSelectedCrmUser(null)}
      />
    </div>
  );
}

// 3. YAYINCI RAPORLARI (YENİ YAYINCI & KULLANICI ADI DEĞİŞİKLİĞİ TAKİBİ)
export function PublisherReportsManagementContent({
  publisherRows = [],
  managers = [],
  loading = false,
  onReload,
  currentAdmin = {},
}) {
  const [activeTab, setActiveTab] = useState("not_in_system");
  const [search, setSearch] = useState("");
  const [diffData, setDiffData] = useState({
    notInSystem: [],
    usernameChanged: [],
    stats: { notInSystemCount: 0, usernameChangedCount: 0, totalReportPublishers: 0, latestReportDate: "" },
  });
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  // Standart modal açma state'i
  const [publisherModal, setPublisherModal] = useState({ open: false, mode: "add", target: null });
  const [publisherForm, setPublisherForm] = useState({
    fullName: "",
    username: "",
    system_username: "",
    phone: "",
    hiddenId: "",
    password: "",
    assignedEmployeeUsername: currentAdmin?.username || "",
    agencyTrackingStatus: "Aktif",
    status: "Aktif",
  });
  const [modalSaving, setModalSaving] = useState(false);

  const loadDiff = useCallback(async () => {
    try {
      setDiffLoading(true);
      setDiffError("");
      const res = await requestJson(API_PUBLISHER_REPORT_DIFF, { cache: "no-store" });
      if (res && res.success) {
        setDiffData({
          notInSystem: res.notInSystem || [],
          usernameChanged: res.usernameChanged || [],
          stats: res.stats || {
            notInSystemCount: (res.notInSystem || []).length,
            usernameChangedCount: (res.usernameChanged || []).length,
            totalReportPublishers: 0,
            latestReportDate: "",
          },
        });
      } else {
        throw new Error(res?.message || "Rapor karşılaştırma verisi alınamadı.");
      }
    } catch (err) {
      setDiffError(err.message || "Rapor verileri taranamadı.");
    } finally {
      setDiffLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiff();
  }, [loadDiff, publisherRows]);

  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openAddFromReport = (item) => {
    const cleanUser = String(item.username || "").replace(/[@\s]+/g, "");
    setPublisherForm({
      fullName: item.fullName || cleanUser,
      username: cleanUser,
      system_username: `wolf.${cleanUser.toLowerCase()}`,
      phone: "",
      hiddenId: item.hiddenId || "",
      password: "123456",
      assignedEmployeeUsername: currentAdmin?.username || "",
      agencyTrackingStatus: "Aktif",
      status: "Aktif",
    });
    setPublisherModal({ open: true, mode: "add", target: item });
  };

  const openEditFromReport = (item) => {
    const cleanNewUser = String(item.newUsername || "").replace(/[@\s]+/g, "");
    const u = item.user || {};
    setPublisherForm({
      id: u.id,
      fullName: u.fullName || cleanNewUser,
      username: cleanNewUser,
      system_username: u.system_username || `wolf.${cleanNewUser.toLowerCase()}`,
      phone: u.phone || "",
      hiddenId: item.hiddenId || u.hiddenId || "",
      password: "",
      assignedEmployeeUsername: u.assignedEmployeeUsername || currentAdmin?.username || "",
      agencyTrackingStatus: u.agencyTrackingStatus || "Aktif",
      status: u.status || "Aktif",
    });
    setPublisherModal({ open: true, mode: "edit", target: item });
  };

  const handlePublisherFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setModalSaving(true);
      if (publisherModal.mode === "add") {
        await requestJson(API_USER_ADD, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...publisherForm, role: "publisher" }),
        });
        alert("Yayıncı başarıyla eklendi.");
      } else {
        await requestJson(API_USER_EDIT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(publisherForm),
        });
        alert("Yayıncı bilgileri güncellendi.");
      }
      setPublisherModal({ open: false, mode: "add", target: null });
      if (onReload) onReload();
      loadDiff();
    } catch (err) {
      alert(err.message || "İşlem başarısız.");
    } finally {
      setModalSaving(false);
    }
  };

  const filteredNotInSystem = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return diffData.notInSystem;
    return diffData.notInSystem.filter((item) => {
      return String(item.hiddenId || "").toLowerCase().includes(q) ||
        String(item.username || "").toLowerCase().includes(q) ||
        String(item.fullName || "").toLowerCase().includes(q);
    });
  }, [diffData.notInSystem, search]);

  const filteredUsernameChanged = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return diffData.usernameChanged;
    return diffData.usernameChanged.filter((item) => {
      return String(item.hiddenId || "").toLowerCase().includes(q) ||
        String(item.oldUsername || "").toLowerCase().includes(q) ||
        String(item.newUsername || "").toLowerCase().includes(q);
    });
  }, [diffData.usernameChanged, search]);

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Yayıncı Takip Modülü"
        title="Yayıncı Raporları (Yeni Yayıncı & Kullanıcı Adı Değişikliği Takibi)"
        desc="Günlük yüklenen raporlardaki yayıncıları sabit TikTok ID üzerinden sistemle karşılaştırır; yeni yayıncıları ve kullanıcı adı değişenleri tespit ederek standart yayıncı sistemine aktarmanızı sağlar."
        action={
          <ActionButton onClick={() => { if (onReload) onReload(); loadDiff(); }} disabled={diffLoading || loading}>
            {diffLoading ? "Taranıyor..." : "🔄 Yeniden Karşılaştır"}
          </ActionButton>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          onClick={() => setActiveTab("not_in_system")}
          className={`cursor-pointer rounded-2xl border p-5 transition-all duration-200 relative overflow-hidden ${
            activeTab === "not_in_system"
              ? "border-amber-400 bg-amber-500/15 shadow-[0_0_24px_rgba(245,158,11,0.22)] ring-1 ring-amber-400/40"
              : "border-white/10 bg-white/[0.03] hover:border-amber-500/40 hover:bg-white/[0.05]"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Sistemde Olmayanlar</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-500/20 text-base">👤➕</span>
          </div>
          <div className="mt-3 text-3xl font-black text-white">{diffData.stats.notInSystemCount}</div>
        </div>

        <div
          onClick={() => setActiveTab("username_changed")}
          className={`cursor-pointer rounded-2xl border p-5 transition-all duration-200 relative overflow-hidden ${
            activeTab === "username_changed"
              ? "border-cyan-400 bg-cyan-500/15 shadow-[0_0_24px_rgba(6,182,212,0.22)] ring-1 ring-cyan-400/40"
              : "border-white/10 bg-white/[0.03] hover:border-cyan-500/40 hover:bg-white/[0.05]"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-cyan-300">Kullanıcı Adı Değişenler</span>
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-500/20 text-base">🔄</span>
          </div>
          <div className="mt-3 text-3xl font-black text-white">{diffData.stats.usernameChangedCount}</div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-white/10 p-4">
          <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} placeholder="TikTok ID veya kullanıcı adı ara..." />
          <ApiNotice loading={diffLoading} error={diffError} onReload={loadDiff} />
        </div>

        {activeTab === "not_in_system" ? (
          <div>
            <div className="hidden grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1.2fr] gap-3 bg-white/[0.04] px-5 py-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white/38 xl:grid">
              <div>TikTok ID</div>
              <div>Rapordaki Kullanıcı Adı</div>
              <div>Rapordaki İsim</div>
              <div>Rapor Tarihi</div>
              <div>İşlem</div>
            </div>
            {filteredNotInSystem.length === 0 ? (
              <EmptyState text={diffLoading ? "Taranıyor..." : "Sistemde olmayan yeni yayıncı bulunamadı. Tüm yayıncılar kayıtlı!"} />
            ) : (
              filteredNotInSystem.map((item, idx) => (
                <div key={item.hiddenId || idx} className="grid gap-3 border-t border-white/10 px-5 py-4 text-sm text-white/75 items-center xl:grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1.2fr]">
                  <div className="font-mono text-cyan-300 font-bold flex items-center gap-2">
                    <span>{item.hiddenId}</span>
                    <button type="button" onClick={() => copyToClipboard(item.hiddenId, `nis-${idx}`)} className="text-[10px] text-white/40 hover:text-white">
                      {copiedId === `nis-${idx}` ? "✓" : "📋"}
                    </button>
                  </div>
                  <div className="font-bold text-amber-300">@{item.username}</div>
                  <div className="text-white/80">{item.fullName || item.username}</div>
                  <div className="font-mono text-xs text-white/50">{item.latestDate || "-"}</div>
                  <div>
                    <ActionButton onClick={() => openAddFromReport(item)}>
                      Sisteme Yayıncı Ekle
                    </ActionButton>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div>
            <div className="hidden grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1.2fr] gap-3 bg-white/[0.04] px-5 py-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white/38 xl:grid">
              <div>TikTok ID</div>
              <div>Eski (Sistemdeki Ad)</div>
              <div>Yeni (Rapordaki Ad)</div>
              <div>Rapor Tarihi</div>
              <div>İşlem</div>
            </div>
            {filteredUsernameChanged.length === 0 ? (
              <EmptyState text={diffLoading ? "Taranıyor..." : "Kullanıcı adı değişen yayıncı bulunamadı."} />
            ) : (
              filteredUsernameChanged.map((item, idx) => (
                <div key={item.hiddenId || idx} className="grid gap-3 border-t border-white/10 px-5 py-4 text-sm text-white/75 items-center xl:grid-cols-[1.5fr_1.5fr_1.5fr_1fr_1.2fr]">
                  <div className="font-mono text-cyan-300 font-bold">{item.hiddenId}</div>
                  <div className="font-mono text-xs text-red-300">@{item.oldUsername}</div>
                  <div className="font-mono text-xs text-emerald-300 font-bold">@{item.newUsername}</div>
                  <div className="font-mono text-xs text-white/50">{item.latestDate || "-"}</div>
                  <div>
                    <ActionButton onClick={() => openEditFromReport(item)}>
                      Kullanıcı Adını Güncelle
                    </ActionButton>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      {/* Standart Ekleme / Düzenleme Modalı */}
      <StandardPublisherModal
        mode={publisherModal.mode}
        open={publisherModal.open}
        form={publisherForm}
        setForm={setPublisherForm}
        managers={managers}
        saving={modalSaving}
        onChange={(e) => setPublisherForm({ ...publisherForm, [e.target.name]: e.target.value })}
        onClose={() => setPublisherModal({ open: false, mode: "add", target: null })}
        onSubmit={handlePublisherFormSubmit}
      />
    </div>
  );
}

// 4. YAYINCI İÇERİKLERİ & EYE EXPERIENCE (ROZETLER, PROFİL, TİKTOK KARTLARI)
export function PublisherEyeExperienceContent({ publisherRows = [], currentAdmin = {} }) {
  const sortedPublishers = useMemo(() => {
    return [...(publisherRows || [])].sort((a, b) => {
      const userA = String(a.username || a.fullName || "").replace(/^@+/, "").trim();
      const userB = String(b.username || b.fullName || "").replace(/^@+/, "").trim();
      return userA.localeCompare(userB, "tr", { sensitivity: "base" });
    });
  }, [publisherRows]);

  const [selectedUsername, setSelectedUsername] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const activePublisher = useMemo(() => {
    if (selectedUsername === "ALL" || !selectedUsername) return null;
    return sortedPublishers.find((p) => String(p.username) === String(selectedUsername)) || null;
  }, [selectedUsername, sortedPublishers]);

  const filteredPublishers = useMemo(() => {
    if (!searchQuery.trim()) return sortedPublishers;
    const q = searchQuery.toLowerCase().replace(/^@+/, "").trim();
    return sortedPublishers.filter((p) => {
      const u = String(p.username || "").toLowerCase();
      const f = String(p.fullName || "").toLowerCase();
      return u.includes(q) || f.includes(q);
    });
  }, [sortedPublishers, searchQuery]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentIndex = useMemo(() => {
    if (!activePublisher) return -1;
    return sortedPublishers.findIndex((p) => String(p.username) === String(activePublisher.username));
  }, [activePublisher, sortedPublishers]);

  const handlePrev = () => {
    if (sortedPublishers.length === 0) return;
    const nextPub = currentIndex <= 0 ? sortedPublishers[sortedPublishers.length - 1] : sortedPublishers[currentIndex - 1];
    if (nextPub) {
      setSelectedUsername(nextPub.username);
      setSearchQuery("@" + String(nextPub.username).replace(/^@+/, "") + " — " + (nextPub.fullName || ""));
    }
  };

  const handleNext = () => {
    if (sortedPublishers.length === 0) return;
    const nextPub = currentIndex === -1 || currentIndex >= sortedPublishers.length - 1 ? sortedPublishers[0] : sortedPublishers[currentIndex + 1];
    if (nextPub) {
      setSelectedUsername(nextPub.username);
      setSearchQuery("@" + String(nextPub.username).replace(/^@+/, "") + " — " + (nextPub.fullName || ""));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Yönetim Paneli"
        title="Yayıncı İçerikleri & Profil Deneyimi"
        desc="Bağlı yayıncılarınızın TikTok profil kartları, persona başarı rozetleri ve canlı yayın içerikleri bu ekranda görüntülenir."
      />

      <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-96" ref={dropdownRef}>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs text-white">
              <span className="text-white/40">🔍</span>
              <input
                type="text"
                value={searchQuery}
                placeholder="Yayıncı ara (örn: @kullanici)"
                onFocus={() => setIsDropdownOpen(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                className="w-full bg-transparent outline-none text-white placeholder-white/30"
              />
            </div>
            {isDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-y-auto rounded-xl border border-white/15 bg-black/95 p-1 shadow-2xl backdrop-blur-xl">
                <div
                  onClick={() => {
                    setSelectedUsername("ALL");
                    setSearchQuery("");
                    setIsDropdownOpen(false);
                  }}
                  className="cursor-pointer px-3 py-2 rounded-lg text-xs font-bold text-yellow-300 hover:bg-white/10"
                >
                  🌟 Tüm Bağlı Yayıncılar
                </div>
                {filteredPublishers.map((p) => (
                  <div
                    key={p.id || p.username}
                    onClick={() => {
                      setSelectedUsername(p.username);
                      setSearchQuery("@" + String(p.username).replace(/^@+/, "") + " — " + (p.fullName || ""));
                      setIsDropdownOpen(false);
                    }}
                    className="cursor-pointer px-3 py-2 rounded-lg text-xs text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    @{p.username} <span className="text-white/40">({p.fullName})</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={handlePrev} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-white hover:border-yellow-500/40">
              ◀ Önceki
            </button>
            <span className="text-xs font-mono text-white/60">
              {currentIndex !== -1 ? `${currentIndex + 1} / ${sortedPublishers.length}` : `${sortedPublishers.length} Yayıncı`}
            </span>
            <button type="button" onClick={handleNext} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-white hover:border-yellow-500/40">
              Sonraki ▶
            </button>
          </div>
        </div>
      </div>

      {activePublisher ? (
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-white/10">
            {activePublisher.image || activePublisher.profileImage ? (
              <img
                src={activePublisher.image || activePublisher.profileImage}
                alt={activePublisher.fullName}
                className="h-24 w-24 rounded-full object-cover border-2 border-yellow-400 shadow-xl"
              />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-yellow-400 bg-black text-2xl font-black text-yellow-300">
                {String(activePublisher.fullName || activePublisher.username || "W").charAt(0).toUpperCase()}
              </div>
            )}

            <div className="text-center sm:text-left">
              <h3 className="text-2xl font-black text-white">{activePublisher.fullName || "-"}</h3>
              <div className="mt-1 font-mono text-sm text-yellow-400">@{activePublisher.username}</div>
              <div className="mt-2 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <span className="rounded-md bg-white/10 px-2 py-0.5 text-xs font-mono text-cyan-300">
                  ID: {activePublisher.hiddenId || "-"}
                </span>
                <span className="rounded-md bg-yellow-500/10 px-2 py-0.5 text-xs font-semibold text-yellow-300">
                  {activePublisher.managerName || activePublisher.assignedEmployeeUsername || activePublisher.representative || "-"}
                </span>
                <StatusBadge status={activePublisher.status || "Aktif"} />
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-center">
              <div className="text-xs font-bold uppercase tracking-wider text-white/40">Kazanılan Elmas</div>
              <div className="mt-2 text-2xl font-black text-yellow-300">
                💎 {Number(activePublisher.performanceDiamonds || activePublisher.currentDiamonds || 0).toLocaleString("tr-TR")}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-center">
              <div className="text-xs font-bold uppercase tracking-wider text-white/40">Canlı Yayın Süresi</div>
              <div className="mt-2 text-2xl font-black text-white">
                {activePublisher.liveHours || 0} Saat
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-center">
              <div className="text-xs font-bold uppercase tracking-wider text-white/40">Yayın Adedi</div>
              <div className="mt-2 text-2xl font-black text-cyan-300">
                {activePublisher.performanceLiveCount || activePublisher.currentPublishCount || 0} Yayın
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedPublishers.slice(0, 18).map((pub) => (
            <Card
              key={pub.id || pub.username}
              className="p-4 cursor-pointer hover:border-yellow-500/40 hover:bg-white/[0.05] transition-all"
            >
              <div
                onClick={() => {
                  setSelectedUsername(pub.username);
                  setSearchQuery("@" + String(pub.username).replace(/^@+/, "") + " — " + (pub.fullName || ""));
                }}
                className="flex items-center gap-3.5"
              >
                {pub.image || pub.profileImage ? (
                  <img src={pub.image || pub.profileImage} alt={pub.fullName} className="h-12 w-12 rounded-full object-cover border border-yellow-400" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-500/20 text-sm font-black text-yellow-300 border border-yellow-400/40">
                    {String(pub.fullName || pub.username || "W").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-bold text-white text-sm truncate">{pub.fullName || "-"}</div>
                  <div className="font-mono text-xs text-yellow-400 truncate">@{pub.username}</div>
                  <div className="mt-1 text-[11px] text-white/40">
                    💎 {Number(pub.performanceDiamonds || pub.currentDiamonds || 0).toLocaleString("tr-TR")} Elmas
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
